import React, { useRef, useState } from "react";
import api, { getToken } from "../lib/api";
import ConfirmDialog from "./ConfirmDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Loader2, Download, Upload, ShieldAlert, Mail,
  History, ChevronDown, ChevronRight, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Backup & Migration card — extracted from AdminSettings for maintainability.
 *
 * Owns:
 *   • JSON export (safe / with-secrets) + the reveal-secrets confirm dialog
 *   • Drop-zone + file-picker import → server-side preview → apply
 *   • "Send test backup" one-shot
 *   • Collapsible Change-history panel + per-row Restore… flow
 *   • Shared rich diff-preview modal (also used for history restore)
 *
 * All server writes here mutate app_settings, so we call `onSettingsChanged`
 * afterward so the parent can re-fetch and re-render the rest of the settings
 * form. This keeps state ownership tight — the parent doesn't need to know
 * anything about the internal preview / history state machine.
 */
export default function SettingsBackupCard({ onSettingsChanged }) {
  // Export
  const [exporting, setExporting] = useState(false);
  const [confirmReveal, setConfirmReveal] = useState(false);
  // Import (file / drop)
  const [importing, setImporting] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [pendingImport, setPendingImport] = useState(null);
  const [restoreHistoryId, setRestoreHistoryId] = useState(null);
  const fileInputRef = useRef(null);
  // Test backup send
  const [sendingTestBackup, setSendingTestBackup] = useState(false);
  const [sendingChangeDigest, setSendingChangeDigest] = useState(false);
  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCap, setHistoryCap] = useState(50);

  const exportSettings = async (withSecrets = false) => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/settings/export?reveal_secrets=${withSecrets ? "true" : "false"}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `sentry-settings-${stamp}${withSecrets ? "-with-secrets" : ""}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(withSecrets ? "Config exported (with secrets — store securely!)" : "Config exported (secrets redacted)");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Shared parser + preview fetch for both file-picker and drop-zone
  const handleImportFile = async (file) => {
    if (!file) return;
    let parsed;
    try {
      const text = await file.text();
      parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object" || typeof parsed.settings !== "object") {
        throw new Error("Missing 'settings' object");
      }
      if (parsed.kind && parsed.kind !== "sentry-cloud-it-settings") {
        throw new Error(`Unexpected kind '${parsed.kind}'`);
      }
    } catch (err) {
      toast.error(`Invalid JSON: ${err.message || "parse error"}`);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await api.post("/settings/import/preview", parsed);
      setImportPreview({ file, parsed, preview: res.data });
      setPendingImport({ file, parsed });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const onImportFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    handleImportFile(file);
  };

  const onDropFile = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      toast.error("Please drop a .json settings backup");
      return;
    }
    handleImportFile(file);
  };

  const runImport = async () => {
    setImporting(true);
    try {
      let res;
      if (restoreHistoryId) {
        res = await api.post(`/settings/history/${restoreHistoryId}/restore`);
      } else {
        if (!pendingImport) return;
        res = await api.post("/settings/import", pendingImport.parsed);
      }
      const { applied_count, skipped_masked, skipped_null, skipped_unknown, changed_keys } = res.data;
      const primary = restoreHistoryId ? (changed_keys?.length ?? applied_count) : applied_count;
      const parts = [`${primary} field(s) ${restoreHistoryId ? "restored" : "applied"}`];
      if (skipped_null?.length) parts.push(`${skipped_null.length} null/redacted skipped`);
      if (skipped_masked?.length) parts.push(`${skipped_masked.length} masked skipped`);
      if (skipped_unknown?.length) parts.push(`${skipped_unknown.length} unknown skipped`);
      toast.success(`${restoreHistoryId ? "Restore" : "Import"} complete — ${parts.join(", ")}`);
      cancelImport();
      if (historyOpen) loadHistory();
      onSettingsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || (restoreHistoryId ? "Restore failed" : "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setPendingImport(null);
    setImportPreview(null);
    setRestoreHistoryId(null);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/settings/history");
      setHistoryItems(r.data.items || []);
      setHistoryCap(r.data.cap || 50);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next && historyItems.length === 0) loadHistory();
  };

  const previewHistoryRestore = async (item) => {
    setPreviewLoading(true);
    try {
      const r = await api.post(`/settings/history/${item.id}/preview`);
      setImportPreview({
        file: { name: `History snapshot · ${new Date(item.created_at).toLocaleString()}` },
        parsed: { exported_at: item.created_at, exported_by: item.created_by },
        preview: r.data,
        isHistory: true,
      });
      setRestoreHistoryId(item.id);
      setPendingImport(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendTestBackupNow = async () => {
    setSendingTestBackup(true);
    try {
      const res = await api.post("/settings/backup-now");
      const n = res.data?.sent_to ?? 0;
      toast.success(n ? `Test backup emailed to ${n} admin(s)` : "No admins configured — no email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test backup send failed");
    } finally {
      setSendingTestBackup(false);
    }
  };

  const sendTestChangeDigestNow = async () => {
    setSendingChangeDigest(true);
    try {
      const res = await api.post("/settings/change-digest-now?window_days=7");
      const n = res.data?.sent_to ?? 0;
      toast.success(n ? `Test change digest emailed to ${n} admin(s)` : "No admins configured — no email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test change digest send failed");
    } finally {
      setSendingChangeDigest(false);
    }
  };

  return (
    <>
      <Card className="bg-white border-slate-200 p-6 shadow-sm" data-testid="import-export-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-brand-primary">Backup & migration</h2>
            <p className="text-xs text-slate-500 mt-1 max-w-md">
              Export the entire runtime config as JSON — useful for backups, cloning between staging/prod, or seeding new MSP tenants. By default secrets are redacted (safe to share). Reveal to include real credentials — store the file encrypted.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => exportSettings(false)}
              disabled={exporting}
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="export-safe-btn"
            >
              {exporting ? <Loader2 className="animate-spin" size={14}/> : <><Download size={14} className="mr-1.5"/>Export (safe)</>}
            </Button>
            <Button
              onClick={() => setConfirmReveal(true)}
              disabled={exporting}
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="export-secrets-btn"
              title="Includes real API keys + passwords. Store the file encrypted."
            >
              <ShieldAlert size={14} className="mr-1.5"/>Export (with secrets)
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={onImportFileChosen}
              data-testid="import-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing || previewLoading}
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="import-file-btn"
            >
              {previewLoading ? <Loader2 className="animate-spin" size={14}/> : <><Upload size={14} className="mr-1.5"/>Choose file…</>}
            </Button>
            <Button
              onClick={sendTestBackupNow}
              disabled={sendingTestBackup}
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="send-test-backup-btn"
              title="Fires the weekly settings backup email once, right now, so you can verify SMTP + confirm the file looks right."
            >
              {sendingTestBackup ? <Loader2 className="animate-spin" size={14}/> : <><Mail size={14} className="mr-1.5"/>Send test backup</>}
            </Button>
            <Button
              onClick={sendTestChangeDigestNow}
              disabled={sendingChangeDigest}
              variant="outline"
              size="sm"
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="send-test-change-digest-btn"
              title="Fires the weekly settings change digest email once, right now, so you can preview the colour-coded HTML before flipping the toggle on."
            >
              {sendingChangeDigest ? <Loader2 className="animate-spin" size={14}/> : <><Mail size={14} className="mr-1.5"/>Send test change digest</>}
            </Button>
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDropFile}
          onClick={() => fileInputRef.current?.click()}
          className={`mt-4 rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            dragOver
              ? "border-brand-accent bg-cyan-50"
              : "border-slate-300 hover:border-slate-400 hover:bg-slate-50"
          }`}
          data-testid="import-dropzone"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
        >
          <Upload size={22} className="mx-auto text-slate-400 mb-2"/>
          <div className="text-sm font-medium text-slate-700">
            {previewLoading ? "Reading backup…" : "Drop a settings JSON here or click to browse"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Preview shows exactly which fields will change before anything is applied.
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-3">
          Auto-backup runs every Monday 09:00 UTC when the <em>Email weekly settings backup</em> toggle below is on. Secrets always redacted.
        </p>

        {/* History panel */}
        <div className="mt-5 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={toggleHistory}
            className="w-full flex items-center justify-between text-left group"
            data-testid="history-toggle-btn"
            aria-expanded={historyOpen}
          >
            <div className="flex items-center gap-2">
              {historyOpen ? <ChevronDown size={16} className="text-slate-500"/> : <ChevronRight size={16} className="text-slate-500"/>}
              <History size={16} className="text-brand-primary"/>
              <span className="text-sm font-semibold text-slate-800 group-hover:text-brand-primary">Change history</span>
              {historyItems.length > 0 && (
                <span className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                  {historyItems.length}{historyItems.length >= historyCap ? "+" : ""}
                </span>
              )}
            </div>
            <span className="text-[11px] text-slate-400">
              Newest first · last {historyCap} kept
            </span>
          </button>

          {historyOpen && (
            <div className="mt-3" data-testid="history-panel">
              {historyLoading ? (
                <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
                  <Loader2 className="animate-spin mr-2" size={14}/>Loading history…
                </div>
              ) : historyItems.length === 0 ? (
                <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-4 text-center" data-testid="history-empty">
                  No history yet — snapshots are captured on every settings write.
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden" data-testid="history-list">
                  {historyItems.map((item) => (
                    <li key={item.id} className="p-3 flex items-start gap-3 hover:bg-slate-50" data-testid={`history-row-${item.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-slate-700">
                            {new Date(item.created_at).toLocaleString()}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                              item.source === "restore"
                                ? "bg-violet-50 border-violet-200 text-violet-700"
                                : item.source === "import"
                                ? "bg-sky-50 border-sky-200 text-sky-700"
                                : "bg-slate-100 border-slate-200 text-slate-600"
                            }`}
                            data-testid={`history-source-${item.source}`}
                          >
                            {item.source}
                          </span>
                          <span className="text-[11px] text-slate-500">by {item.created_by || "unknown"}</span>
                        </div>
                        {item.changed_keys?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {item.changed_keys.slice(0, 6).map((k) => (
                              <span key={k} className="text-[10px] font-mono bg-slate-100 border border-slate-200 text-slate-700 rounded px-1.5 py-0.5">{k}</span>
                            ))}
                            {item.changed_keys.length > 6 && (
                              <span className="text-[10px] text-slate-500">+{item.changed_keys.length - 6} more</span>
                            )}
                          </div>
                        )}
                        {item.note && (
                          <div className="text-[11px] text-slate-500 mt-1 italic">{item.note}</div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => previewHistoryRestore(item)}
                        disabled={previewLoading || importing}
                        className="border-slate-300 text-slate-700 hover:bg-slate-100 flex-shrink-0"
                        data-testid={`history-restore-btn-${item.id}`}
                        title="Preview a diff of restoring this snapshot"
                      >
                        <RotateCcw size={13} className="mr-1"/>Restore…
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-[11px] text-slate-400 mt-2">
                Secrets (API keys, SMTP passwords, webhook secrets) are always nulled in snapshots — restoring never clobbers your current credentials.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Confirm: export WITH secrets */}
      <ConfirmDialog
        open={confirmReveal}
        onOpenChange={setConfirmReveal}
        title="Export config with real secrets?"
        description="The downloaded JSON will contain API keys, SMTP passwords, and webhook secrets in plaintext. Only proceed if you're storing the file encrypted (e.g., a password manager or an encrypted volume). Prefer 'Export (safe)' for anything shared over email or committed to git."
        confirmLabel="Yes, export with secrets"
        destructive
        onConfirm={() => exportSettings(true)}
        testId="export-secrets-confirm"
      />

      {/* Rich diff preview — shared by file-import and history-restore */}
      <Dialog open={!!importPreview} onOpenChange={(v) => !v && cancelImport()}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle>
              {importPreview?.isHistory ? "Restore from history" : "Review import"} — {importPreview?.file?.name}
            </DialogTitle>
            <DialogDescription>
              {importPreview?.parsed?.exported_at
                ? `${importPreview?.isHistory ? "Captured" : "Exported"} ${new Date(importPreview.parsed.exported_at).toLocaleString()}${importPreview.parsed.exported_by ? ` by ${importPreview.parsed.exported_by}` : ""}.`
                : "Preview the changes below, then apply."}
            </DialogDescription>
          </DialogHeader>

          {importPreview && (
            <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4 text-sm">
              {/* Stat pills */}
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="preview-stat-change">
                  {importPreview.preview.will_change_count} will change
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  {importPreview.preview.unchanged_count} unchanged
                </span>
                {importPreview.preview.skipped_null?.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200" title="Null / redacted values in the JSON — skipped so real secrets aren't clobbered">
                    {importPreview.preview.skipped_null.length} null/redacted skipped
                  </span>
                )}
                {importPreview.preview.skipped_masked?.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200" title="Masked '•…' values in the JSON — skipped">
                    {importPreview.preview.skipped_masked.length} masked skipped
                  </span>
                )}
                {importPreview.preview.skipped_unknown?.length > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200" title={importPreview.preview.skipped_unknown.join(", ")}>
                    {importPreview.preview.skipped_unknown.length} unknown keys ignored
                  </span>
                )}
              </div>

              {/* Diff table */}
              {importPreview.preview.will_change_count === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-center text-slate-600" data-testid="preview-no-changes">
                  No changes — this backup matches your current config.
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-xs" data-testid="preview-diff-table">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Field</th>
                        <th className="text-left px-3 py-2 font-medium w-2/5">Before</th>
                        <th className="text-left px-3 py-2 font-medium w-2/5">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.preview.will_change.map((row) => (
                        <tr key={row.key} className="border-t border-slate-200" data-testid={`preview-row-${row.key}`}>
                          <td className="px-3 py-2 font-mono text-slate-700 align-top">
                            {row.key}
                            {row.is_secret && (
                              <span className="ml-1 text-[10px] text-amber-600" title="Secret field">⚠︎</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-rose-700 align-top break-all">
                            {row.before === null || row.before === undefined || row.before === "" ? <em className="text-slate-400 not-italic">—</em> : String(row.before)}
                          </td>
                          <td className="px-3 py-2 font-mono text-emerald-700 align-top break-all">
                            {row.after === null || row.after === undefined || row.after === "" ? <em className="text-slate-400 not-italic">—</em> : String(row.after)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <p className="text-[11px] text-slate-500">
                Existing secrets are preserved automatically — masked/null values in the backup are skipped.
              </p>
            </div>
          )}

          <DialogFooter className="pt-4 border-t border-slate-200">
            <Button variant="ghost" onClick={cancelImport} disabled={importing} data-testid="preview-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={runImport}
              disabled={importing || (importPreview?.preview?.will_change_count === 0)}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white"
              data-testid="preview-apply-btn"
            >
              {importing ? <><Loader2 className="animate-spin mr-2" size={14}/>Applying…</> : `${importPreview?.isHistory ? "Restore" : "Apply"} ${importPreview?.preview?.will_change_count ?? 0} change(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
